import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Modal, Typography, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SwiftUniLogo from "../../../assets/images/swiftuniLogoo.svg";

export default function EndModal({ open, onClose, sentences = [], intervalMs = 3000, testTitle = "PTE Mock Test", navigateTo = "/mt-score" }) {
  const navigate = useNavigate();
  
  // Determine if this is an IELTS test
  const isIELTS = testTitle.toLowerCase().includes('ielts');
  
  const safeSentences = useMemo(() => {
    // If custom sentences provided, use them
    if (Array.isArray(sentences) && sentences.length) {
      return sentences;
    }
    
    // Otherwise, use defaults based on test type
    return isIELTS ? [
      "Processing your test responses...",
      "Analyzing your answers...",
      "Evaluating your performance...",
      "Calculating your scores...",
      "Finalizing your test results..."
    ] : [
      "Processing your speaking responses...",
      "Analyzing writing tasks...",
      "Evaluating reading comprehension...",
      "Scoring listening section...",
      "Finalizing your mock test results..."
    ];
  }, [sentences, isIELTS]);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const fadeTimeoutRef = useRef();
  const [progress, setProgress] = useState(0);

  // Rotate sentences with smooth fade and fixed height container
  useEffect(() => {
    if (!open) return;
    
    // Reset progress when modal opens
    setProgress(0);
    
    let slowProgressInterval;
    
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev < 92) {
          // Normal progress until 92%
          return Math.min(prev + 2, 92);
        } else if (prev === 92) {
          // Start slow progress when reaching 92%
          slowProgressInterval = setInterval(() => {
            setProgress((current) => {
              if (current >= 92 && current < 98) {
                return current + 1;
              }
              return current;
            });
          }, intervalMs); // Much slower - every intervalMs (3 seconds by default)
        }
        return prev;
      });
    }, intervalMs / 10); // Normal interval
    
    const sentenceInterval = setInterval(() => {
      setVisible(false);
      fadeTimeoutRef.current = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % safeSentences.length);
        setVisible(true);
      }, 280);
    }, Math.max(2200, intervalMs));
    
    return () => {
      clearInterval(sentenceInterval);
      clearInterval(progressInterval);
      if (slowProgressInterval) clearInterval(slowProgressInterval);
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    };
  }, [open, safeSentences.length, intervalMs]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
    borderRadius: 3,
    p: 3,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  };

  return (
    <Modal 
      open={open} 
      onClose={(event, reason) => {
        // Prevent closing on backdrop click - only allow programmatic close
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      disableEscapeKeyDown={true}
      sx={{ 
        backdropFilter: "blur(4px)",
        backgroundColor: "rgba(0, 0, 0, 0.05)"
      }}
    >
      <Box sx={style}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Box component="img" src={SwiftUniLogo} alt="SwiftUni" sx={{ height: 28 }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: "#2d2d2d" }}>
              {testTitle}
            </Typography>
          </Box>
          
          {/* Progress indicator */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
            <CircularProgress 
              size={24} 
              thickness={4} 
              sx={{ color: "#6a5acd" }} 
            />
            <Typography variant="body2" sx={{ color: "#555", fontSize: 14 }}>
              Finalizing your mock test... {progress}%
            </Typography>
          </Box>
          
          {/* Animated progress bar */}
          <Box sx={{ width: "100%", position: "relative" }}>
            <Box sx={{ 
              width: "100%", 
              height: 6, 
              bgcolor: "#f0f0f5", 
              borderRadius: 3, 
              overflow: "hidden" 
            }}>
              <Box
                sx={{
                  width: `${progress}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #6a5acd 0%, #996cfe 100%)",
                  borderRadius: 3,
                  transition: "width 0.5s ease",
                }}
              />
            </Box>
          </Box>

          {/* Message with fixed height and fade */}
          <Box sx={{ 
            textAlign: "center", 
            minHeight: 80, 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center", 
            py: 1.5,
            gap: 0.5
          }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "#2d2d2d", mb: 0.5 }}>
              Please wait a moment
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: "#555", 
                opacity: visible ? 1 : 0, 
                transition: "opacity 280ms ease", 
                lineHeight: 1.5,
                minHeight: 40,
                display: "flex",
                alignItems: "center"
              }}
            >
              {safeSentences[currentIndex]}
            </Typography>
          </Box>
          
          {/* Footer note */}
          <Typography 
            variant="caption" 
            sx={{ 
              color: isIELTS ? "#d32f2f" : "#888", 
              textAlign: "center", 
              mt: 1,
              fontWeight: isIELTS ? 600 : 400
            }}
          >
            {isIELTS 
              ? "⚠️ Important: Please wait here and do not close this page. Closing the page may result in loss of your test results."
              : "This may take a few moments. You can wait here or view all scores."
            }
          </Typography>
          
          {/* Navigation link - Hide for IELTS */}
          {!isIELTS && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate(navigateTo)}
                sx={{
                  color: "#6a5acd",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  "&:hover": {
                    color: "#996cfe",
                  }
                }}
              >
                View all mock test scores
              </Link>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}